import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, Users, FileText, Coins, ChartBar, MagnifyingGlass, FloppyDisk, ShieldStar } from "@phosphor-icons/react";
import { TOOLS, PILLARS } from "@/lib/tools";

const WINDOWS = [
  { key: "month", label: "This Month" },
  { key: "30d", label: "30 Days" },
  { key: "all", label: "All Time" },
];

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon size={18} weight="duotone" />
        <span className="text-xs font-semibold tracking-[0.1em] uppercase">{label}</span>
      </div>
      <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900 font-mono">{value}</p>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [window, setWindow] = useState("month");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const loadAnalytics = useCallback(async (w) => {
    try { const res = await api.get(`/admin/analytics?window=${w}`); setAnalytics(res.data); }
    catch { toast.error("Failed to load analytics"); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
        setStats(s.data);
        setUsers(u.data);
        const d = {};
        u.data.forEach((x) => { d[x.user_id] = { ai_enabled: x.ai_enabled, ai_monthly_cap: x.ai_monthly_cap ?? "" }; });
        setDrafts(d);
        await loadAnalytics("month");
      } catch (e) {
        toast.error("Admin data unavailable");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadAnalytics]);

  const changeWindow = async (w) => { setWindow(w); await loadAnalytics(w); };

  const setDraft = (uid, patch) => setDrafts((d) => ({ ...d, [uid]: { ...d[uid], ...patch } }));

  const saveAi = async (uid) => {
    setSavingId(uid);
    const draft = drafts[uid];
    const cap = draft.ai_monthly_cap === "" || draft.ai_monthly_cap === null ? null : Number(draft.ai_monthly_cap);
    try {
      const res = await api.put(`/admin/users/${uid}/ai`, { ai_enabled: draft.ai_enabled, ai_monthly_cap: cap });
      setUsers((list) => list.map((x) => (x.user_id === uid ? { ...x, ai_enabled: res.data.ai_enabled, ai_monthly_cap: res.data.ai_monthly_cap } : x)));
      setDraft(uid, { ai_monthly_cap: res.data.ai_monthly_cap ?? "" });
      toast.success("AI controls updated");
    } catch { toast.error("Update failed"); }
    finally { setSavingId(null); }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return !q || (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q) || (u.organization_name || "").toLowerCase().includes(q);
  });

  const maxCount = analytics?.heatmap?.[0]?.count || 1;
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

  if (loading)
    return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto w-full py-10 px-6 lg:px-10">
        <div className="flex items-center gap-2 text-slate-500">
          <ShieldStar size={18} weight="duotone" />
          <p className="text-xs font-semibold tracking-[0.12em] uppercase">Administration · Restricted</p>
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Master Admin Dashboard</h1>
        <p className="mt-2 text-slate-500">System health, AI governance and anonymized organizational friction signals.</p>

        {/* System usage analytics */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8" data-testid="admin-stats">
          <StatCard testid="stat-users" icon={Users} label="Total Users" value={stats.total_users} />
          <StatCard testid="stat-subs" icon={ShieldStar} label="Active Subs" value={stats.active_subscriptions} />
          <StatCard testid="stat-docs" icon={FileText} label="Documents" value={stats.total_documents} />
          <StatCard testid="stat-calls" icon={ChartBar} label="AI Calls" value={stats.total_ai_calls} />
          <StatCard testid="stat-tokens" icon={Coins} label="Est. Tokens" value={stats.estimated_tokens.toLocaleString()} />
        </div>

        {/* Concierge analytics heatmap */}
        <section className="mt-10 bg-white border border-slate-200 rounded-sm p-6" data-testid="admin-analytics">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <div>
              <h2 className="font-mono text-sm font-bold uppercase tracking-[0.1em] text-slate-700">Concierge Friction Heatmap</h2>
              <p className="text-sm text-slate-500 mt-0.5">Most-recommended frameworks — anonymized (no user identity stored).</p>
            </div>
            <div className="inline-flex rounded-sm bg-slate-100 p-1" data-testid="analytics-window">
              {WINDOWS.map((w) => (
                <button key={w.key} data-testid={`window-${w.key}`} onClick={() => changeWindow(w.key)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${window === w.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <p className="font-mono text-xs text-slate-400 mb-4">{analytics?.total_queries || 0} queries in window</p>
          {(!analytics?.heatmap || analytics.heatmap.length === 0) ? (
            <p className="py-8 text-center text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm">No concierge queries in this window yet.</p>
          ) : (
            <div className="space-y-2.5" data-testid="heatmap-rows">
              {analytics.heatmap.map((row) => {
                const tool = TOOLS[row.tool_type];
                const pillar = PILLARS[tool?.pillar] || PILLARS.OPERATIONS;
                return (
                  <div key={row.tool_type} data-testid={`heatmap-${row.tool_type}`} className="flex items-center gap-3">
                    <span className="w-56 shrink-0 text-sm font-medium text-slate-800 truncate">{row.tool_name}</span>
                    <div className="flex-1 h-6 bg-slate-100 rounded-sm overflow-hidden">
                      <div className={`h-full ${pillar.bar}`} style={{ width: `${Math.max(6, (row.count / maxCount) * 100)}%` }} />
                    </div>
                    <span className="w-10 text-right font-mono text-sm font-semibold text-slate-700">{row.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* User directory + AI overrides */}
        <section className="mt-10" data-testid="admin-users">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="font-mono text-sm font-bold uppercase tracking-[0.1em] text-slate-700">User Directory & Tzu Credit Override</h2>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email, name, org…"
                className="h-9 w-64 rounded-sm border border-slate-300 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-left min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-semibold">User</th>
                  <th className="px-4 py-3 font-semibold">Organization</th>
                  <th className="px-4 py-3 font-semibold">Registered</th>
                  <th className="px-4 py-3 font-semibold">Subscription</th>
                  <th className="px-4 py-3 font-semibold">Docs</th>
                  <th className="px-4 py-3 font-semibold">AI Access</th>
                  <th className="px-4 py-3 font-semibold">Monthly Cap</th>
                  <th className="px-4 py-3 font-semibold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => {
                  const d = drafts[u.user_id] || { ai_enabled: true, ai_monthly_cap: "" };
                  const dirty = d.ai_enabled !== u.ai_enabled || String(d.ai_monthly_cap ?? "") !== String(u.ai_monthly_cap ?? "");
                  return (
                    <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">{u.name || "—"}{u.is_admin && <span className="font-mono text-[9px] bg-slate-900 text-white px-1.5 py-0.5 rounded-sm">ADMIN</span>}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{u.organization_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${u.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{u.subscription_status || "none"}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-slate-700">{u.documents_count}</td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`ai-toggle-${u.user_id}`}
                          onClick={() => setDraft(u.user_id, { ai_enabled: !d.ai_enabled })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${d.ai_enabled ? "bg-emerald-600" : "bg-slate-300"}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${d.ai_enabled ? "translate-x-6" : "translate-x-1"}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          data-testid={`ai-cap-${u.user_id}`}
                          type="number" min="0"
                          value={d.ai_monthly_cap}
                          onChange={(e) => setDraft(u.user_id, { ai_monthly_cap: e.target.value })}
                          placeholder="∞"
                          className="h-9 w-24 rounded-sm border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="text-[10px] text-slate-400 mt-0.5 font-mono">used {u.ai_calls_this_month}</p>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          data-testid={`ai-save-${u.user_id}`}
                          onClick={() => saveAi(u.user_id)}
                          disabled={!dirty || savingId === u.user_id}
                          className="inline-flex items-center gap-1.5 rounded-sm bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-40"
                        >
                          {savingId === u.user_id ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} />} Save
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">No users match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Layout>
  );
}
